import React, { useState, useEffect, useCallback } from 'react';
import { getItems, createItem, updateItem, deleteItem } from '../api';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { SkeletonList } from '../components/Skeleton';
import BarcodeScanner from '../components/BarcodeScanner';
import ItemSearch from '../components/ItemSearch';

const emptyItem = {
  friendlyName: '', hebCode: '', hebExactName: '', variant: '', upc: '',
  isStaple: true, defaultQuantity: 1
};

export default function Settings({ showToast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  const loadItems = useCallback(async () => {
    try {
      const data = await getItems();
      setItems(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);

  const handleSave = async () => {
    if (!editItem.friendlyName.trim()) { showToast('Name is required'); return; }
    try {
      if (isNew) {
        await createItem(editItem);
        showToast('Item created');
      } else {
        await updateItem(editItem._id, editItem);
        showToast('Item updated');
      }
      setEditItem(null);
      loadItems();
    } catch (e) {
      showToast('Error saving item');
    }
  };

  const handleScanResult = (result) => {
    setShowScanner(false);
    if (result.found && result.source === 'local') {
      // Item already exists in our DB — open it for editing
      showToast(`Found: ${result.item.friendlyName}`);
      setEditItem({ ...result.item });
      setIsNew(false);
    } else if (result.found && result.source === 'openfoodfacts') {
      // Found on Open Food Facts — pre-fill for creation
      showToast('Found product info — confirm & save');
      setEditItem({
        ...emptyItem,
        friendlyName: result.item.friendlyName || '',
        hebExactName: result.item.hebExactName || '',
        variant: result.item.variant || '',
        upc: result.item.upc || ''
      });
      setIsNew(true);
    } else {
      // Not found anywhere — let user name it manually
      showToast('Product not found — enter details manually');
      setEditItem({
        ...emptyItem,
        upc: result.upc || ''
      });
      setIsNew(true);
    }
  };

  const handleDelete = async (id) => {
    await deleteItem(id);
    loadItems();
    showToast('Item deleted');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Items & Staples</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowScanner(true)}
            className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-xl flex items-center gap-2 font-medium"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
            </svg>
            Scan
          </button>
          <button
            onClick={() => { setEditItem({ ...emptyItem }); setIsNew(true); }}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl flex items-center gap-2 font-medium"
          >
            <PlusIcon className="w-5 h-5" /> Add Item
          </button>
        </div>
      </div>

      {/* Search items */}
      <div className="mb-4">
        <ItemSearch
          onSelect={(item) => { setEditItem({ ...item }); setIsNew(false); }}
          placeholder="Search items to edit..."
          actionIcon="edit"
        />
      </div>

      {loading ? (
        <SkeletonList count={5} />
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>No items configured yet</p>
          <p className="text-sm mt-1">Add your staple H-E-B items to get started</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <div key={item._id} className="bg-gray-800 rounded-xl px-4 py-3 border border-gray-700 flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{item.friendlyName}</span>
                  {item.isStaple && <span className="text-xs bg-blue-600/20 text-blue-400 px-2 py-0.5 rounded">Staple</span>}
                </div>
                {item.hebExactName && <p className="text-gray-400 text-sm mt-0.5">{item.hebExactName}</p>}
                <div className="text-gray-500 text-xs mt-0.5">
                  {item.variant && `${item.variant} · `}
                  {item.hebCode && `Code: ${item.hebCode} · `}
                  {item.upc && `UPC: ${item.upc} · `}
                  Qty: {item.defaultQuantity}
                </div>
              </div>
              <div className="flex gap-2 ml-3">
                <button
                  onClick={() => { setEditItem({ ...item }); setIsNew(false); }}
                  className="p-2 text-gray-400 hover:text-white"
                >
                  <PencilIcon className="w-5 h-5" />
                </button>
                <button onClick={() => handleDelete(item._id)} className="p-2 text-gray-400 hover:text-red-400">
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Barcode Scanner */}
      {showScanner && (
        <BarcodeScanner
          onResult={handleScanResult}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Edit/Create modal */}
      {editItem && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setEditItem(null)}>
          <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-md border border-gray-700 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">{isNew ? 'Add Item' : 'Edit Item'}</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-400 block mb-1">Friendly Name *</label>
                <input
                  type="text" value={editItem.friendlyName}
                  onChange={e => setEditItem({ ...editItem, friendlyName: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">H-E-B Exact Name</label>
                <input
                  type="text" value={editItem.hebExactName}
                  onChange={e => setEditItem({ ...editItem, hebExactName: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-400 block mb-1">H-E-B Code</label>
                  <input
                    type="text" value={editItem.hebCode}
                    onChange={e => setEditItem({ ...editItem, hebCode: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Variant</label>
                  <input
                    type="text" value={editItem.variant}
                    onChange={e => setEditItem({ ...editItem, variant: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">UPC / Barcode</label>
                <input
                  type="text" value={editItem.upc || ''}
                  onChange={e => setEditItem({ ...editItem, upc: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="e.g. 041220966295"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Default Quantity</label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setEditItem({ ...editItem, defaultQuantity: Math.max(1, (editItem.defaultQuantity || 1) - 1) })}
                      className="w-10 h-10 flex items-center justify-center bg-gray-600 hover:bg-gray-500 rounded-xl text-white text-xl font-bold shrink-0"
                    >
                      −
                    </button>
                    <input
                      type="number" value={editItem.defaultQuantity} min="1"
                      onChange={e => {
                        const val = e.target.value;
                        setEditItem({ ...editItem, defaultQuantity: val === '' ? '' : Math.max(1, parseInt(val) || 1) });
                      }}
                      className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-2.5 text-white text-center focus:outline-none focus:ring-2 focus:ring-red-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button
                      type="button"
                      onClick={() => setEditItem({ ...editItem, defaultQuantity: (editItem.defaultQuantity || 1) + 1 })}
                      className="w-10 h-10 flex items-center justify-center bg-gray-600 hover:bg-gray-500 rounded-xl text-white text-xl font-bold shrink-0"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox" checked={editItem.isStaple}
                      onChange={e => setEditItem({ ...editItem, isStaple: e.target.checked })}
                      className="w-5 h-5 rounded bg-gray-700 border-gray-600 text-red-500 focus:ring-red-500"
                    />
                    <span className="text-sm">Staple Item</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setEditItem(null)} className="flex-1 bg-gray-700 hover:bg-gray-600 py-3 rounded-xl font-medium">
                Cancel
              </button>
              <button onClick={handleSave} className="flex-1 bg-red-600 hover:bg-red-700 py-3 rounded-xl font-semibold">
                {isNew ? 'Create' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
